import { useCallback, useRef, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import {
  addMessage,
  updateMessage,
  setError,
  clearError,
  setNewChat,
  selectActiveSessionMessages,
  selectChatUIState,
  selectCurrentStreamingId,
  selectActiveSessionId,
  selectApiStatus,
  setConnectionState,
  setApiStatus,
  resetStreamingState,
  ApiStatus,
  WELCOME_MESSAGE_ID,
  setTypingAnimation,
} from 'modules/chatbot/slices/chatbotSlice';
import { MESSAGE_LIMIT } from 'modules/chatbot/constants/constants';
import { Sender } from 'modules/chatbot/enums/Sender';
import {
  UseChatReturn,
  UseChatOptions,
  ChatError,
  ChatMessage,
  MessageStatus,
  EventType,
  SSEMessage,
  ActionType,
} from 'modules/chatbot/types/chatbot.types';
import { mapSessionToConversationId } from 'modules/chatbot/utils/sessionUtils';
import {
  SSERequestPayload,
  chatbotSSEService,
} from 'modules/chatbot/services/chatbotSSEService';
import {
  validateMessageContent,
  sanitizeMessageContent,
} from 'modules/chatbot/utils/validation';
import { createChatError } from 'modules/chatbot/utils/errorHandling';

export const useChatSSE = (options: UseChatOptions = {}): UseChatReturn => {
  const { autoConnect = true, metadata = {} } = options;

  const dispatch = useAppDispatch();
  const messages = useAppSelector(selectActiveSessionMessages);
  const uiState = useAppSelector(selectChatUIState);
  const currentStreamingId = useAppSelector(selectCurrentStreamingId);
  const activeSessionId = useAppSelector(selectActiveSessionId);
  const apiStatus = useAppSelector(selectApiStatus);

  // Ensure session exists on mount
  useEffect(() => {
    if (!activeSessionId) {
      dispatch(setNewChat());
    }
  }, [dispatch, activeSessionId]);

  const retryCountRef = useRef<Record<string, number>>({});
  const lastMessageTimeRef = useRef<number>(0);
  const hasReceivedFirstMessage = useRef<boolean>(false);

  // Handler functions for different SSE message types
  const handleStatusMessage = useCallback(
    (content: any) => {
      // Display status content in the chat bubble
      if (content) {
        dispatch(
          updateMessage({
            content: content,
            eventType: EventType.STATUS,
          })
        );
      }
    },
    [dispatch]
  );

  const handleSourcesMessage = useCallback((data: any) => {
    // Handle sources/references messages
    console.log('Sources message:', data);
    // TODO: Implement sources handling logic
  }, []);

  const handleActionMessage = useCallback(
    (data: any) => {
      switch (data) {
        case ActionType.PLAN_RECOMMENDER:
          const href = buildPlanRecommenderUrl(
            metadata?.context?.benefitGuideContent,
            location
          );
          dispatch(
            updateMessage({
              content: ` <a href="${href}" target="_blank" rel="noopener noreferrer">Plan Recommender</a>`,
              eventType: EventType.ACTION,
            })
          );
          break;

        default:
          break;
      }
    },
    [dispatch, metadata?.context?.benefitGuideContent]
  );

  const handleStreamMessage = useCallback(
    (content: string) => {
      if (content) {
        dispatch(
          updateMessage({
            content,
            eventType: EventType.CHUNK,
          })
        );
        dispatch(
          setTypingAnimation({
            isTyping: true,
            messageId: currentStreamingId,
            chunkCount: 0,
          })
        );
      }
    },
    [dispatch, currentStreamingId]
  );

  const handleCompleteMessage = useCallback(
    (content?: any) => {
      // Simple completion - trust backend to send proper COMPLETE events
      let parsedSources = null;

      // Parse sources if content is a JSON string
      if (typeof content === 'string') {
        try {
          parsedSources = JSON.parse(content);
        } catch (error) {
          console.warn('Failed to parse sources JSON:', error);
          parsedSources = null;
        }
      } else {
        parsedSources = content;
      }

      dispatch(
        updateMessage({
          content: '',
          eventType: EventType.COMPLETE,
          sources: parsedSources,
        })
      );
    },
    [dispatch]
  );

  // Returns: "/<urlHash>/<employerName>/plan-recommender/medical?revision=..&previewToken=.."
  // Falls back to "/plan-recommender/medical" if urlHash/employerName are missing.
  const buildPlanRecommenderUrl = (
    benefitGuideContent: {
      urlHash?: string;
      employerName?: string;
      latestRevision?: number | string;
    } = {},
    location: Location
  ): string => {
    const { urlHash, employerName, latestRevision } = benefitGuideContent || {};

    // Build path segments defensively
    const segments = [
      urlHash && encodeURIComponent(String(urlHash)),
      employerName && encodeURIComponent(String(employerName)),
      'plan-recommender',
      'medical',
    ].filter(Boolean) as string[];

    const path = '/' + segments.join('/');

    // Build query params
    const params: Record<string, string> = {};
    if (latestRevision != null) params.revision = String(latestRevision);

    const previewToken = new URLSearchParams(location?.search || '').get(
      'previewToken'
    );
    if (previewToken) params.previewToken = previewToken;

    const qs = Object.keys(params).length
      ? `?${new URLSearchParams(params).toString()}`
      : '';

    return path + qs;
  };

  const handleErrorMessage = useCallback(() => {
    dispatch(
      updateMessage({
        content:
          'I encountered an issue processing your request. Please try again',
        eventType: EventType.ERROR,
      })
    );
  }, [dispatch]);

  // Initialize chat connection
  useEffect(() => {
    if (autoConnect) {
      // Set up connection state monitoring
      chatbotSSEService.setEventHandlers({
        onConnect: () => {
          dispatch(setConnectionState(true));
        },
        onDisconnect: () => {
          dispatch(setConnectionState(false));
        },
        onComplete: () => {
          handleCompleteMessage();
        },
        onMessage: (message: SSEMessage) => {
          if (message.data) {
            const { type, content } = message.data;

            const messageHandlers: Record<EventType, () => void> = {
              [EventType.STATUS]: () => {
                handleStatusMessage(content);
              },
              [EventType.SOURCES]: () => {
                handleSourcesMessage(content);
              },
              [EventType.CHUNK]: () => {
                handleStreamMessage(content);
              },
              [EventType.ACTION]: () => {
                handleActionMessage(content);
              },
              [EventType.RESPONSE]: () => {
                handleStreamMessage(content);
              },
              [EventType.ERROR]: () => {
                handleErrorMessage();
              },
              [EventType.COMPLETE]: () => {
                handleCompleteMessage(content);
              },
            };

            const handler = messageHandlers[type as EventType];
            if (handler) {
              handler();
            } else {
              console.warn('Unknown SSE message type:', type);
            }
          }
        },
        onError: (error: ChatError) => {
          console.error('SSE Connection Error:', error);

          // Create user-friendly error message for backend connection issues
          const userFriendlyError = createChatError(
            error.code,
            'Unable to connect to the chat service. The backend service may be unavailable.',
            {
              originalError: error,
              backendError: true,
              timestamp: Date.now(),
            }
          );

          // IMPORTANT: Update the message BEFORE clearing streaming state
          // This ensures currentStreamingId is still available for updateMessage
          dispatch(
            updateMessage({
              content:
                userFriendlyError.userMessage || userFriendlyError.message,
              eventType: EventType.ERROR,
            })
          );

          // Now clear streaming state and update API status
          dispatch(resetStreamingState());
          dispatch(setApiStatus(ApiStatus.ERROR));
          dispatch(setConnectionState(false));
          dispatch(setError(userFriendlyError));
        },
      });
    }

    return () => {
      // Cleanup on unmount
      chatbotSSEService.disconnect();
    };
  }, [
    autoConnect,
    dispatch,
    currentStreamingId,
    handleCompleteMessage,
    handleStreamMessage,
    handleStatusMessage,
    handleSourcesMessage,
    handleErrorMessage,
    handleActionMessage,
  ]);

  // Send message function with SSE streaming
  const sendMessage = useCallback(
    async (content: string): Promise<void> => {
      try {
        // Validate and sanitize content
        const sanitizedContent = sanitizeMessageContent(content);
        const validation = validateMessageContent(sanitizedContent);

        if (!validation.isValid) {
          const validationError = createChatError(
            'VALIDATION_ERROR',
            Object.values(validation.errors).join('; ')
          );
          dispatch(setError(validationError));
          return;
        }

        // Clear any existing errors and set loading status
        dispatch(clearError());
        dispatch(setApiStatus(ApiStatus.LOADING));

        // Build message history for context
        // Send last MESSAGE_LIMIT individual messages
        // Exclude welcome message from history
        const history = messages
          .filter(
            (msg) => msg.id !== WELCOME_MESSAGE_ID || msg.content.trim() !== ''
          )
          .slice(-MESSAGE_LIMIT)
          .map((msg) => ({
            role:
              msg.sender === Sender.USER
                ? ('user' as const)
                : ('assistant' as const),
            content: msg.content,
          }));

        // Validate required fields
        if (!metadata?.organizationId || !metadata?.employerId) {
          throw new Error('Organization ID and Employer ID are required');
        }

        // Create SSE request payload
        const ssePayload: SSERequestPayload = {
          message: sanitizedContent,
          conversationId: mapSessionToConversationId(activeSessionId),
          history,
          llmContext: metadata?.context,
        };

        const startTime = Date.now();

        const userMessage: Omit<
          ChatMessage,
          'id' | 'timestamp' | 'createdAt' | 'updatedAt'
        > = {
          sender: Sender.USER,
          content: sanitizedContent,
          status: MessageStatus.COMPLETED,
        };
        dispatch(addMessage({ ...userMessage } as any));

        // Reset flag for new message
        hasReceivedFirstMessage.current = false;

        // Create bot message placeholder for streaming BEFORE sending SSE
        const botMessage: Omit<
          ChatMessage,
          'id' | 'timestamp' | 'createdAt' | 'updatedAt'
        > = {
          sender: Sender.BOT,
          content: '',
          status: MessageStatus.PENDING,
        };

        dispatch(addMessage({ ...botMessage } as any));

        // Send SSE message
        await chatbotSSEService.sendMessage(ssePayload);

        const responseTime = Date.now() - startTime;
        lastMessageTimeRef.current = responseTime;
      } catch (error) {
        const chatError = createChatError(
          'UNKNOWN_ERROR',
          error instanceof Error ? error.message : 'Unknown error occurred',
          { operation: 'sendMessage', originalError: error }
        );

        // Update the current message with error content and status
        dispatch(
          updateMessage({
            content: chatError.userMessage || chatError.message,
            eventType: EventType.ERROR,
          })
        );

        dispatch(setError(chatError));
        dispatch(setApiStatus(ApiStatus.ERROR));
        dispatch(resetStreamingState());
      }
    },
    [dispatch, messages, metadata, activeSessionId]
  );

  // Clear chat function
  const clearChat = useCallback(() => {
    // Disconnect current SSE connection
    chatbotSSEService.disconnect();
    dispatch(setNewChat());
    dispatch(resetStreamingState());
    retryCountRef.current = {};
  }, [dispatch]);

  // Clear error function
  const clearErrorHandler = useCallback(() => {
    dispatch(clearError());
  }, [dispatch]);

  return {
    messages,
    sendMessage,
    clearChat,
    isLoading: apiStatus === ApiStatus.LOADING,
    error: uiState.error,
    clearError: clearErrorHandler,
    uiState,
  };
};
